// Cadastro de uma credencial de processador para o merchant.
//
// É o passo de credenciais que antes vivia no wizard de meio de pagamento — a diferença é que
// aqui ele acontece UMA vez: a credencial cadastrada atende quantos meios forem necessários,
// inclusive de white labels diferentes.
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { ProviderForm } from "@/components/commons"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useCreateMerchantProcessor } from "@/services/admin/merchant-processors"
import type { ProcessorGatewayMetadata } from "@/types/admin-processor-config"

import { ProcessorCertificateSelect } from "./processor-certificate-select"

const prefix = "pages.adminMerchantProcessors"

type CreateMerchantProcessorDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Metadata dos processadores; só os `writable` recebem cadastro. */
  metadata: ProcessorGatewayMetadata[]
}

export const CreateMerchantProcessorDialog = ({
  open,
  onOpenChange,
  metadata,
}: CreateMerchantProcessorDialogProps) => {
  const { t } = useTranslation()

  const [gateway, setGateway] = useState("")
  const [name, setName] = useState("")
  const [nameMissing, setNameMissing] = useState(false)
  const [certificateId, setCertificateId] = useState("")
  const nameInputRef = useRef<HTMLInputElement>(null)

  const writable = metadata.filter((item) => item.writable)
  const selected = writable.find(
    (item) => item.gateway === gateway
  )

  const reset = () => {
    setGateway("")
    setName("")
    setNameMissing(false)
    setCertificateId("")
  }

  const { mutate, isPending } = useCreateMerchantProcessor(
    () => {
      reset()
      onOpenChange(false)
    }
  )

  const handleOpenChange = (next: boolean) => {
    if (!next) reset()
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t(`${prefix}.create.title`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${prefix}.create.description`)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="processor-gateway">
              {t(`${prefix}.create.gateway`)}
            </Label>
            <Select
              value={gateway}
              onValueChange={(next) => {
                setGateway(next)
                // O certificado vive FORA do ProviderForm, então o `key` que remonta
                // as credenciais não o alcança: sem limpar aqui, escolher o
                // certificado num banco e trocar para um adquirente mandaria
                // `mtlsCertificateId` num processador que não usa mTLS.
                setCertificateId("")
              }}
            >
              <SelectTrigger id="processor-gateway">
                <SelectValue
                  placeholder={t(
                    `${prefix}.create.gatewayPlaceholder`
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {writable.map((item) => (
                  <SelectItem
                    key={item.gateway}
                    value={item.gateway}
                  >
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="processor-name">
              {t(`${prefix}.create.name`)}
            </Label>
            <Input
              id="processor-name"
              ref={nameInputRef}
              value={name}
              maxLength={255}
              aria-invalid={nameMissing}
              aria-describedby="processor-name-hint"
              placeholder={t(
                `${prefix}.create.namePlaceholder`
              )}
              onChange={(event) => {
                setName(event.target.value)
                if (nameMissing) setNameMissing(false)
              }}
            />
            <p
              id="processor-name-hint"
              role={nameMissing ? "alert" : undefined}
              className={
                nameMissing
                  ? "text-xs text-red-600"
                  : "text-xs text-muted-foreground"
              }
            >
              {nameMissing
                ? t(`${prefix}.create.nameRequired`)
                : t(`${prefix}.create.nameHint`)}
            </p>
          </div>

          {/* Só os bancos com mTLS: oferecer o combo num adquirente prometeria um
              vínculo que o backend não aceita.

              Aqui, e não só na edição: sem isto o operador cadastra a credencial,
              volta para a lista, abre o detalhe e só então descobre que falta
              vincular o certificado — e uma credencial de banco sem certificado não
              processa nada. */}
          {selected?.supportsCertificate && (
            <ProcessorCertificateSelect
              value={certificateId || null}
              disabled={isPending}
              onChange={setCertificateId}
            />
          )}

          {selected ? (
            // Remonta a cada processador: os campos são outros, e reaproveitar o
            // estado enviaria credencial de um banco no formato de outro.
            <ProviderForm
              key={selected.gateway}
              fields={selected.fields}
              mode="create"
              saving={isPending}
              submitLabel={t(`${prefix}.create.submit`)}
              onSubmit={(provider) => {
                // Sem nome, tres credenciais Itau ficam indistinguiveis na
                // lista e no combo do meio de pagamento — o backend tambem
                // recusa, mas o erro chegaria depois de digitar o segredo.
                if (name.trim().length === 0) {
                  setNameMissing(true)
                  nameInputRef.current?.focus()
                  return
                }
                mutate({
                  gateway: selected.gateway,
                  name: name.trim(),
                  // O certificado viaja no `provider`, como o metadata declara
                  // (campo CERTIFICATE_REF). O backend resolve a entrada pelo
                  // merchant e recusa a de outro.
                  // Guarda dupla: além de limpar na troca de banco, o payload só
                  // carrega o certificado se o processador escolhido usar mTLS.
                  provider:
                    selected.supportsCertificate &&
                    certificateId
                      ? {
                          ...provider,
                          mtlsCertificateId: certificateId,
                        }
                      : provider,
                })
              }}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
